import { cancelInvocation } from '@wix/ambassador-agentmaker-invocations-v1-invocation/http';
import { Box } from '@wix/design-system';
import React, { useRef, useState } from 'react';
import { ModalId, RUN_TASK_WIDGET_ID, RunTaskWidgetAction } from '../constants';
import { useApprovals, useBi, useEntries, useTranslation } from '../hooks';
import { getAllDetailsPayload } from '../utils';
import { BiLoggerProvider } from './BiLoggerProvider';
import { EntriesList } from './EntriesList';
import { StopButton } from './StopButton';
import { TranslationsProvider } from './TranslationsProvider';
import { ViewAllDetailsButton } from './ViewAllDetailsButton';
const RunTaskWidgetComp = _ref => {
  let {
    data,
    context,
    config
  } = _ref;
  const {
    t,
    isLoading: isTranslationsLoading
  } = useTranslation();
  const widgetRef = useRef(null);
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const {
    biClick
  } = useBi();
  const {
    onWidgetAction
  } = context;
  const httpClient = context.httpClient;
  const webActions = context.webActions;
  const {
    isRestoring,
    metaSiteId,
    biOrigin
  } = data;
  const {
    resolvedApprovalIds,
    isEditing: isEditingApproval,
    editApproval,
    acceptApproval,
    rejectApproval
  } = useApprovals({
    context
  });
  const {
    entries,
    hasCompletedInvocation,
    invocationId
  } = useEntries({
    data,
    context,
    config,
    isStopping,
    isEditingApproval,
    hasTimedOut,
    resolvedApprovalIds,
    widgetRef
  });
  const reportInvocationBiClick = origin => {
    biClick({
      origin,
      entityType: 'invocation',
      entityId: invocationId
    });
  };
  const reportApprovalBiClick = (origin, approvalId) => {
    biClick({
      origin,
      entityType: 'approval',
      entityId: approvalId
    });
  };
  const handleViewDetails = _ref2 => {
    let {
      title,
      date,
      requestPayload,
      resultPayload
    } = _ref2;
    reportInvocationBiClick('view_details');
    webActions.bmOpenModal == null || webActions.bmOpenModal({
      componentId: ModalId.ViewDetails,
      componentParams: {
        title,
        date,
        requestPayload,
        resultPayload,
        biOrigin,
        biName: 'view_details'
      }
    });
  };
  const handleViewAllDetails = () => {
    const resultPayload = getAllDetailsPayload({
      widgetData: data,
      invocationId,
      entries
    });
    reportInvocationBiClick('view_all_details');
    webActions.bmOpenModal == null || webActions.bmOpenModal({
      componentId: ModalId.ViewDetails,
      componentParams: {
        title: t('view-details.modal.title'),
        resultPayload,
        payloadCollapsed: 3,
        biOrigin,
        biName: 'view_all_details'
      }
    });
  };
  const handleApprove = async entry => {
    const approvalId = entry.approvalId;
    if (approvalId && invocationId) {
      reportApprovalBiClick('approve', approvalId);
      acceptApproval({
        approvalId,
        invocationId
      });
    }
  };
  const handleReject = async entry => {
    const approvalId = entry.approvalId;
    if (approvalId && invocationId) {
      reportApprovalBiClick('reject', approvalId);
      rejectApproval({
        approvalId,
        invocationId
      });
    }
  };
  const handleEditApproval = async entry => {
    const approvalId = entry.approvalId;
    if (approvalId) {
      reportApprovalBiClick('edit', approvalId);
      editApproval({
        approvalId,
        title: entry.title,
        biOrigin
      });
    }
  };
  const handleStop = async () => {
    setIsStopping(true);
    try {
      reportInvocationBiClick('stop');
      if (invocationId) {
        await httpClient.request(cancelInvocation({
          invocationId
        }));
      }
      onWidgetAction(RUN_TASK_WIDGET_ID, {
        action: RunTaskWidgetAction.Stopped,
        value: {
          invocationId
        }
      });
    } catch {
      setIsStopping(false);
    }
  };
  const handleTimeout = () => {
    setHasTimedOut(true);
  };
  const showStopButton = !hasCompletedInvocation && !isStopping && !hasTimedOut;
  return /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "SP2",
    ref: widgetRef
  }, /*#__PURE__*/React.createElement(EntriesList, {
    entries: entries,
    resolvedApprovalIds: resolvedApprovalIds,
    invocationId: invocationId,
    metaSiteId: metaSiteId,
    onApprove: handleApprove,
    onReject: handleReject,
    onEditApproval: handleEditApproval,
    onViewDetails: handleViewDetails,
    onTimeout: handleTimeout
  }), !isRestoring && !isTranslationsLoading && /*#__PURE__*/React.createElement(Box, {
    align: "space-between",
    verticalAlign: "middle",
    gap: "SP2"
  }, /*#__PURE__*/React.createElement(ViewAllDetailsButton, {
    onClick: handleViewAllDetails
  }), showStopButton && /*#__PURE__*/React.createElement(StopButton, {
    onStop: handleStop
  })));
};
export const RunTaskWidget = props => {
  const {
    data,
    context
  } = props;

  // In a real chat environment `locale` is provided in a context (but untyped?) and when we render
  // outside chat we pass in locale as part of widget data.
  const locale = (context == null ? void 0 : context.locale) ?? data.locale;
  return /*#__PURE__*/React.createElement(BiLoggerProvider, {
    metaSiteId: data.metaSiteId
  }, /*#__PURE__*/React.createElement(TranslationsProvider, {
    locale: locale
  }, /*#__PURE__*/React.createElement(RunTaskWidgetComp, props)));
};
//# sourceMappingURL=RunTaskWidget.js.map